import * as React from "react";
import { cn } from "@/lib/utils";
import { discordIcon } from "@/lib/assets";
import { Button } from "@/shared/ui/button";
import type { AnalyticsElementId } from "@/lib/analytics/analyticsElementId";
import { deriveAnalyticsElementId } from "@/lib/analytics/deriveAnalyticsElementId";
import { Card } from "@/shared/ui/card";

type PlatformPartnersProps = React.HTMLAttributes<HTMLElement> & {
  baseAnalyticsElementId: AnalyticsElementId;
};

type Partner = {
  analyticsSuffix: string;
  name: string;
  description: string;
  discordLink: string;
  ctaText: string;
};

const partners: Partner[] = [
  {
    analyticsSuffix: "superordinary-link",
    name: "SuperOrdinary",
    description: "US community of women influencers.",
    discordLink: "https://discord.gg/36h8m3qX",
    ctaText: "Join Discord",
  },
  {
    analyticsSuffix: "superclip-link",
    name: "SuperClip",
    description: "Global meme-driven clipping community.",
    discordLink: "https://discord.gg/QGbSFwR5XU",
    ctaText: "Join Discord",
  },
];

export function PlatformPartners({ baseAnalyticsElementId, className, ...props }: PlatformPartnersProps) {
  return (
    <Card
      asChild
      variant="marketing-veil"
      padding="marketing"
      className={cn(
        "relative flex w-full flex-col items-center justify-center gap-6 min-h-[var(--layout-platform-card-min-height)] px-[var(--layout-platform-card-padding-x)] py-[var(--layout-platform-card-padding-y)] text-(--color-text-primary) md:gap-10 xl:h-[var(--layout-platform-card-height-xl)]",
        className,
      )}
    >
      <section {...props}>
        {/* Section Title */}
        <h2 className="text-heading-md text-center text-(--color-text-primary) md:text-heading-lg">
          Clipping Platform Partners
        </h2>

        {/* Partner Cards Container */}
        <div className="grid w-full max-w-6xl grid-cols-1 gap-6 md:grid-cols-2">
          {partners.map((partner) => (
            <PartnerCard
              key={partner.name}
              analyticsElementId={deriveAnalyticsElementId(baseAnalyticsElementId, partner.analyticsSuffix)}
              {...partner}
            />
          ))}
        </div>
      </section>
    </Card>
  );
}

function PartnerCard({
  name,
  description,
  discordLink,
  ctaText,
  analyticsElementId,
}: Partner & { analyticsElementId: AnalyticsElementId }) {
  return (
    <div className="flex flex-col items-center gap-6 rounded-2xl bg-surface-tertiary p-6">
      {/* Partner Name */}
      <div className="flex flex-col items-center gap-4">
        <h3 className="text-heading-sm text-center text-(--color-text-primary)">{name}</h3>
        <p className="text-body-md text-center text-(--color-text-secondary)">{description}</p>
      </div>

      {/* Discord CTA */}
      <Button analyticsElementId={analyticsElementId} variant="marketing" size="lg" className="px-6" asChild>
        <a href={discordLink} target="_blank" rel="noopener noreferrer">
          <span className="mr-2 inline-flex h-[18px] w-6 items-center justify-center">
            <img
              src={discordIcon}
              alt="Discord"
              className="h-[18px] w-6 object-contain"
              loading="lazy"
              decoding="async"
            />
          </span>
          {ctaText}
        </a>
      </Button>
    </div>
  );
}
